import { Any } from 'cosmjs-types/google/protobuf/any'

import { CommissionRates, ValidatorDescription } from '@/types'

import { encodeCoin, encodeMessage, encodeString } from './proto'

export interface CreateValidatorParams {
  delegator_address: string
  validator_address: string
  pubkey: Any
  value?: { denom: string; amount: string }
  description: ValidatorDescription
  commission?: CommissionRates
  min_self_delegation?: string
}

function encodeDescriptionMsg(desc: ValidatorDescription): Uint8Array {
  const parts: number[] = []
  parts.push(...encodeString(1, desc.moniker))
  if (desc.identity) parts.push(...encodeString(2, desc.identity))
  if (desc.website) parts.push(...encodeString(3, desc.website))
  if (desc.security_contact)
    parts.push(...encodeString(4, desc.security_contact))
  if (desc.details) parts.push(...encodeString(5, desc.details))
  return Uint8Array.from(parts)
}

function encodeCommissionRatesMsg(rates: CommissionRates): Uint8Array {
  return Uint8Array.from([
    ...encodeString(1, rates.rate),
    ...encodeString(2, rates.max_rate),
    ...encodeString(3, rates.max_change_rate),
  ])
}

function encodeAnyMessage(msg: Any): Uint8Array {
  const typeUrl = msg.typeUrl ?? ''
  const valueBytes = msg.value ?? new Uint8Array()
  return Uint8Array.from([
    ...encodeString(1, typeUrl),
    ...encodeMessage(2, valueBytes),
  ])
}

// Commission rates must be passed as fixed-point integers (1e18 precision).
// CLI converts decimal values automatically, but when sending protobuf directly
// we have to provide already scaled integer strings.
const DEFAULT_COMMISSION_RATES: CommissionRates = {
  rate: '100000000000000000',
  max_rate: '200000000000000000',
  max_change_rate: '10000000000000000',
}

const DEFAULT_MIN_SELF_DELEGATION = '1'

export function buildCreateValidatorMsg(params: CreateValidatorParams): Any {
  const commission: CommissionRates =
    params.commission ?? DEFAULT_COMMISSION_RATES
  const minSelfDelegation =
    params.min_self_delegation ?? DEFAULT_MIN_SELF_DELEGATION

  const descriptionBytes = encodeDescriptionMsg(params.description)
  const commissionBytes = encodeCommissionRatesMsg(commission)
  const pubkeyBytes = encodeAnyMessage(params.pubkey)

  const parts: number[] = []
  parts.push(...encodeMessage(1, descriptionBytes))
  parts.push(...encodeMessage(2, commissionBytes))
  parts.push(...encodeString(3, minSelfDelegation))
  parts.push(...encodeString(4, params.delegator_address))
  parts.push(...encodeString(5, params.validator_address))
  parts.push(...encodeMessage(6, pubkeyBytes))

  if (params.value) {
    parts.push(
      ...encodeMessage(7, encodeCoin(params.value.denom, params.value.amount)),
    )
  }

  const value = Uint8Array.from(parts)
  return Any.fromPartial({
    typeUrl: '/cosmos.staking.v1beta1.MsgCreateValidator',
    value,
  })
}

// Native token staking operations

export interface NativeDelegateParams {
  delegatorAddress: string
  validatorAddress: string
  amount: { denom: string; amount: string }
}

export interface NativeUndelegateParams {
  delegatorAddress: string
  validatorAddress: string
  amount: { denom: string; amount: string }
}

export interface NativeRedelegateParams {
  delegatorAddress: string
  validatorSrcAddress: string
  validatorDstAddress: string
  amount: { denom: string; amount: string }
}

export function buildNativeDelegateMsg(params: NativeDelegateParams): Any {
  const value = Uint8Array.from([
    ...encodeString(1, params.delegatorAddress),
    ...encodeString(2, params.validatorAddress),
    ...encodeMessage(3, encodeCoin(params.amount.denom, params.amount.amount)),
  ])

  return Any.fromPartial({
    typeUrl: '/cosmos.staking.v1beta1.MsgDelegate',
    value,
  })
}

export function buildNativeUndelegateMsg(params: NativeUndelegateParams): Any {
  const value = Uint8Array.from([
    ...encodeString(1, params.delegatorAddress),
    ...encodeString(2, params.validatorAddress),
    ...encodeMessage(3, encodeCoin(params.amount.denom, params.amount.amount)),
  ])

  return Any.fromPartial({
    typeUrl: '/cosmos.staking.v1beta1.MsgUndelegate',
    value,
  })
}

export function buildNativeRedelegateMsg(params: NativeRedelegateParams): Any {
  const value = Uint8Array.from([
    ...encodeString(1, params.delegatorAddress),
    ...encodeString(2, params.validatorSrcAddress),
    ...encodeString(3, params.validatorDstAddress),
    ...encodeMessage(4, encodeCoin(params.amount.denom, params.amount.amount)),
  ])

  return Any.fromPartial({
    typeUrl: '/cosmos.staking.v1beta1.MsgBeginRedelegate',
    value,
  })
}

export default buildCreateValidatorMsg
